import { CheckCircle2, Circle, Calendar, Flag, Edit2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../utils/cn';
import { Button } from './Button';

const priorityColors = {
    low: 'bg-blue-100 text-blue-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800',
};

const categoryColors = {
    work: 'bg-purple-100 text-purple-800',
    personal: 'bg-green-100 text-green-800',
    shopping: 'bg-pink-100 text-pink-800',
    health: 'bg-teal-100 text-teal-800',
    other: 'bg-gray-100 text-gray-800',
};

export function TaskCard({ task, onToggleComplete, onEdit, onDelete }) {
    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !task.completed;

    return (
        <div className={cn(
            'bg-white rounded-xl shadow-sm border p-4 transition-all duration-200',
            'hover:shadow-md',
            task.completed ? 'opacity-75 border-gray-200' : 'border-gray-200',
            isOverdue && 'border-red-300 bg-red-50'
        )}>
            <div className="flex items-start gap-3">
                <button
                    onClick={() => onToggleComplete(task.id, !task.completed)}
                    className={cn(
                        'mt-1 flex-shrink-0 transition-colors',
                        task.completed ? 'text-primary-600' : 'text-gray-400 hover:text-primary-600'
                    )}
                >
                    {task.completed ? (
                        <CheckCircle2 className="w-6 h-6" />
                    ) : (
                        <Circle className="w-6 h-6" />
                    )}
                </button>

                <div className="flex-1 min-w-0">
                    <h3 className={cn(
                        'font-semibold text-gray-900 truncate',
                        task.completed && 'line-through text-gray-500'
                    )}>
                        {task.title}
                    </h3>

                    {task.description && (
                        <p className={cn(
                            'text-sm text-gray-600 mt-1 line-clamp-2',
                            task.completed && 'text-gray-400'
                        )}>
                            {task.description}
                        </p>
                    )}

                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        {task.priority && (
                            <span className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                                priorityColors[task.priority]
                            )}>
                                <Flag className="w-3 h-3" />
                                {task.priority}
                            </span>
                        )}

                        {task.category && (
                            <span className={cn(
                                'px-2 py-0.5 rounded-full text-xs font-medium',
                                categoryColors[task.category]
                            )}>
                                {task.category}
                            </span>
                        )}

                        {task.dueDate && (
                            <span className={cn(
                                'inline-flex items-center gap-1 text-xs',
                                isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'
                            )}>
                                <Calendar className="w-3 h-3" />
                                {format(new Date(task.dueDate), 'MMM d, yyyy')}
                                {isOverdue && ' (Overdue)'}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(task)}
                        className="p-2"
                    >
                        <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(task.id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}